import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { EventService } from '../events/event.service';
import { NavController } from '@ionic/angular';
import { UserInfoService } from './user-info-service';
import { Semaphore } from 'src/app/utilities/semaphore/semaphore.utility';
import { EnvironmentConfigService } from '../environment-config-service/environment-config.service';
import { AuthenticationApiConfiguration } from 'src/app/api/proxy/auth/authentication-api-configuration';
import {
  localAccountLoginUserPost,
  localAccountForgotPasswordRequestPost,
  localAccountResetForgotPasswordPost,
  localAccountChangePasswordPost,
  localAccountChangeEmailPost,
  localAccountRequestOtpPost,
  localAccountVerifyOtpAsyncPost,
  localAccountDeleteAccountDelete,
  localAccountLinkedSocialAccountGet,
  localAccountRequestAccountLinkPost,
  localAccountLinkAccountPost,
  localAccountUnlinkAccountPost,
  localAccountRefreshTokenPost,
  userSetDefaultLanuagePost
} from 'src/app/api/proxy/auth/functions';
import {
  AccessTokenModel, ChangeEmailAddressRequestModel, ChangePasswordRequestModel, ForgotPasswordRequestModel, LinkSocialAccountRequestModel, LoginRequestModel,
  RefreshTokenRequestModel, RequestOtpModel, RequestOtpResponseModel, ResetPasswordRequestModel, SocialAccountModel, UnlinkSocialAccountRequestModel, VerifyOtpRequestModel
} from 'src/app/api/proxy/auth/authentication-models';
import { firstValueFrom, timeout, TimeoutError } from 'rxjs';
import { BiometricLoginDetail, UserData } from './user-data.model';
import { Capacitor } from '@capacitor/core';
import { SystemLogService } from '../systemlog-service/systemlog.service';
import { PromptService } from '../promtp-service/prompt.service';
import { Browser } from '@capacitor/browser';
import { environment } from 'src/environments/environment';
import { TranslatorService } from '../translator-service/translator.service';
import { LocaleService } from '../locale-service/locale.service';
import { matchSupportedLocale, normalizeCultureTag } from '../locale-service/locale.util';
import { Network } from '@capacitor/network';
import VivaScoreConnect from 'src/app/plugins/vivascore-connect/vivascore-connect-plugin';
import { BaseApiService } from '../base-api-service/base-api.service';
import { WeightDisplayService } from '../weight-display-service/weight-display.service';

/** Result of {@link AuthService.login}; avoids duplicate error modals (network vs invalid credentials). */
export interface LoginResult {
  success: boolean;
  /** True when login() already showed an error (e.g. timeout / unreachable server). */
  errorAlreadyShown?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {
  private NETWORK_TIMEOUT = 20000;
  private tokenRefreshRetry = 0;
  private REFRESH_TIMEOUT = 15000;
  private readonly MAX_REFRESH_RETRIES = 6;
  private readonly RETRY_BACKOFF_BASE = 1000; // in milliseconds
  private readonly RETRY_BACKOFF_MAX = 60000;
  private readonly DEFAULT_RT_TTL_SEC = 90 * 24 * 60 * 60; // 90 days in seconds

  constructor(
    public navCtrl: NavController,
    private _EnvironmentConfigService: EnvironmentConfigService,
    private http: HttpClient,
    private authCfg: AuthenticationApiConfiguration,
    protected _PromptService: PromptService,
    private _localeService: LocaleService,
    private _weightDisplay: WeightDisplayService,
  ) {
    super(_PromptService);
    EventService.appPaused.subscribe(async () => {
      this.clearRefresh();
    });
  }


  public async init(): Promise<void> {
    await this.setupRefreshTimer();
  }

  public async tokenExpired(): Promise<boolean> {
    const user = await UserInfoService.getUserInfo();
    if (!user?.tokenExpiry) return true;
    const exp = new Date(user.tokenExpiry).getTime();
    const now = Date.now();
    const SKEW_MS = 60_000 * 15; // 15 minute skew
    return now >= (exp - SKEW_MS);
  }


  public async token(): Promise<string | null> {
    const userInfo = await UserInfoService.getUserInfo();
    if (userInfo && userInfo.access_token) {
      return userInfo.access_token;
    }
    return null;
  }

  public async saveBiometricLogin(countryCode: string, email: string, password: string) {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }
    const biometricLogin: BiometricLoginDetail = {
      username: email,
      password: password,
      countrycode: countryCode
    };
    await SecureStorageService.set(SecureStorageKey.BiometricLogin, JSON.stringify(biometricLogin));
  }

  public async getBiometricLogin(): Promise<BiometricLoginDetail | null> {
    if (Capacitor.getPlatform() === 'web') {
      return null;
    }

    const userData = await SecureStorageService.get(
      SecureStorageKey.BiometricLogin
    );

    if (!userData || userData.length === 0 || userData === '') {
      return null;
    }
    const userInfo = JSON.parse(userData) as BiometricLoginDetail;
    if (!userInfo) {
      return null;
    }

    return userInfo;
  }

  public async clearBiometricLogin(): Promise<void> {
    const bioData = await this.getBiometricLogin();
    if (bioData) {
      await SecureStorageService.set(SecureStorageKey.BiometricLogin, '');
      await SecureStorageService.remove(SecureStorageKey.BiometricLogin);
    }
  }

  public async login(emailAddress: string, pasw: string): Promise<LoginResult> {
    // Guard: require non-empty credentials
    if (!emailAddress || !pasw || emailAddress.trim().length === 0 || pasw.trim().length === 0) {
      return { success: false };
    }
    try {
      const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");
      if (!envConfig) {
        return { success: false };
      }

      const loginRequest: LoginRequestModel = {
        applicationId: envConfig.authenticateOptions!.appId!,
        applicationScope: envConfig.authenticateOptions!.scope!,
        emailAddress: emailAddress,
        password: pasw,
      };

      let response: AccessTokenModel | null;

      try {
        const obs = localAccountLoginUserPost(this.http, this.authCfg.rootUrl, { body: loginRequest });
        const httpResponse = await firstValueFrom(obs.pipe(timeout(this.NETWORK_TIMEOUT)));
        response = httpResponse.body;

      } catch (error: unknown) {
        if (error instanceof TimeoutError) {
          await this._PromptService.showNetworkConnectionError(error, 'login');
          return { success: false, errorAlreadyShown: true };
        }
        const status = (error as { status?: number })?.status;
        // 4xx from auth (wrong password, invalid grant, etc.) — single message from login UI
        if (typeof status === 'number' && status >= 400 && status < 500) {
          return { success: false };
        }
        await this._PromptService.showNetworkConnectionError(error, 'login');
        return { success: false, errorAlreadyShown: true };
      }

      const oldUser = await UserInfoService.getUserInfo();

      if (response && response.access_token && response.access_token.length > 0 && !response.error) {
        const tokenResponse = response as AccessTokenModel;
        const token = this.parseJwt(tokenResponse.access_token!);
        // Convert `exp` to milliseconds and create a Date object for expiry
        const tokenExpiryUtc = new Date(token.exp * 1000).toISOString();

        let rtTtlSec = 0;//tokenResponse.refresh_token_expires_in;
        if (!rtTtlSec || rtTtlSec <= 0) {
          rtTtlSec = this.DEFAULT_RT_TTL_SEC;
        }


        const nowMs = Date.now();
        const refreshTokenExpiryIso = rtTtlSec != null ? new Date(nowMs + rtTtlSec * 1000).toISOString() : (oldUser?.refreshTokenExpiry ?? null);

        const effectiveLanguage = await this.resolveEffectiveUserLanguage(token.language);

        // Subtract 2 minutes from the expiry time
        const userInfo: UserData = {
          sub: token.oid,
          given_name: token.given_name,
          family_name: token.family_name,
          name: token.name,
          access_token: tokenResponse.access_token ?? oldUser?.access_token ?? undefined,
          refresh_token: tokenResponse.refresh_token ?? oldUser?.refresh_token ?? undefined,
          refreshTokenExpiry: refreshTokenExpiryIso,
          tokenExpiry: tokenExpiryUtc,
          tokenClaims: token,
          email: token.userEmailAddress!,
          language: effectiveLanguage,
          systemFunctions: token.systemFunctions,
          emailVerified: tokenResponse.emailVerified,
          localAccount: token.localAccount,
          socialIssuer: token.idp,
          b2cFlow: token.tfp
        };

        if (oldUser && oldUser.sub !== userInfo.sub) {
          await this.clearUserData(true);
        }
        await UserInfoService.setUserInfo(userInfo);
        await this.setUserLoggedInAsync();
        await this.logDebug('user logged in ' + JSON.stringify(userInfo));
        return { success: true };
      }
    } catch (error) {
      console.error('Unexpected error:', error);
    }
    return { success: false };
  }

  public async forgotPasswordRequest(email: string): Promise<boolean> {
    const model: ForgotPasswordRequestModel = { emailAddress: email };
    const result = await this.callApi(
      localAccountForgotPasswordRequestPost(this.http, this.authCfg.rootUrl, { body: model }),
      this.NETWORK_TIMEOUT,
      'forgotPasswordRequest'
    );
    return result !== null;
  }

  public async resetForgotPassword(email: string, password: string, otp: number): Promise<boolean> {
    const model: ResetPasswordRequestModel = { emailAddress: email, newPassword: password, otp: otp };
    const result = await this.callApi(
      localAccountResetForgotPasswordPost(this.http, this.authCfg.rootUrl, { body: model }),
      this.NETWORK_TIMEOUT,
      'resetForgotPassword'
    );
    if (result !== null) {
      await this.clearBiometricLogin();
      return true;
    }
    return false;
  }

  public async changePassword(password: string) {
    const model: ChangePasswordRequestModel = { newPassword: password };
    const result = await this.callApi(
      localAccountChangePasswordPost(this.http, this.authCfg.rootUrl, { body: model }),
      this.NETWORK_TIMEOUT,
      'changePassword'
    );
    if (result !== null) {
      if (Capacitor.isNativePlatform()) {
        const bioPassword = await this.getBiometricLogin();
        if (bioPassword) {
          await this.saveBiometricLogin(bioPassword.countrycode, bioPassword.username, password);
        }
      }
      return true;
    }
    return false;
  }

  /**
   * Sync preferred culture to the auth identity claim after profile save.
   * Soft-fails (logs) so a VitalScore profile update is not rolled back.
   */
  public async setDefaultLanguage(cultureOrCode: string): Promise<boolean> {
    const matched = matchSupportedLocale(
      cultureOrCode,
      this._localeService.getAvailableLocales(),
      this._localeService.getDefaultCulture(),
    );
    try {
      const result = await this.callApi(
        userSetDefaultLanuagePost(this.http, this.authCfg.rootUrl, {
          body: { languageCode: matched.culture },
        }),
        this.NETWORK_TIMEOUT,
        'setDefaultLanguage',
      );
      if (result !== null) {
        await this._localeService.setCulture(matched.culture, {
          persistUserData: true,
          fireTranslateEvents: false,
        });
        return true;
      }
      return false;
    } catch (error) {
      SystemLogService.logError(error, 'AuthService', 'setDefaultLanguage');
      return false;
    }
  }

  public async changeEmail(emailaddress: string) {
    const model: ChangeEmailAddressRequestModel = { newEmail: emailaddress };
    const result = await this.callApi(
      localAccountChangeEmailPost(this.http, this.authCfg.rootUrl, { body: model }),
      this.NETWORK_TIMEOUT,
      'changeEmail'
    );
    if (result !== null) {
      if (Capacitor.isNativePlatform()) {
        const bioPassword = await this.getBiometricLogin();
        if (bioPassword) {
          await this.saveBiometricLogin(bioPassword.countrycode, emailaddress, bioPassword.password);
        }
      }
      return true;
    }
    return false;
  }

  public async registerProfile(tokenResponse: AccessTokenModel): Promise<boolean> {
    try {
      const oldUser = await UserInfoService.getUserInfo();

      if (tokenResponse && tokenResponse.access_token && tokenResponse.access_token.length > 0 && !tokenResponse.error) {
        const token = this.parseJwt(tokenResponse.access_token);
        // Convert `exp` to milliseconds and create a Date object for expiry
        const tokenExpiryUtc = new Date(token.exp * 1000).toISOString();

        let rtTtlSec = 0;//tokenResponse.refresh_token_expires_in;
        if (!rtTtlSec || rtTtlSec <= 0) {
          rtTtlSec = this.DEFAULT_RT_TTL_SEC;
        }
        const nowMs = Date.now();
        const refreshTokenExpiryIso = rtTtlSec != null ? new Date(nowMs + rtTtlSec * 1000).toISOString() : (oldUser?.refreshTokenExpiry ?? null);
        const effectiveLanguage = await this.resolveEffectiveUserLanguage(token.language);
        // Subtract 2 minutes from the expiry time
        const userInfo: UserData = {
          sub: token.oid,
          given_name: token.given_name,
          family_name: token.family_name,
          name: token.name,
          access_token: tokenResponse.access_token ?? oldUser?.access_token ?? null,
          refresh_token: tokenResponse.refresh_token ?? oldUser?.refresh_token ?? null,
          refreshTokenExpiry: refreshTokenExpiryIso,
          tokenExpiry: tokenExpiryUtc,
          tokenClaims: token,
          email: token.userEmailAddress!,
          language: effectiveLanguage,
          systemFunctions: token.systemFunctions,
          emailVerified: tokenResponse.emailVerified,
          localAccount: token.localAccount,
          socialIssuer: token.idp,
          b2cFlow: token.tfp
        };

        if (oldUser && oldUser.sub !== userInfo.sub) {
          await this.clearUserData(true);
        }
        await UserInfoService.setUserInfo(userInfo);
        await this.setUserLoggedInAsync();
        return true; // Login successful
      } else {
        throw new Error("Unexpected error occurred");
      }
    } catch (error) {
      SystemLogService.logError(error);
      return false;
    }
  }

  public async requestOTP(model: RequestOtpModel): Promise<RequestOtpResponseModel | null> {
    const obs = localAccountRequestOtpPost(this.http, this.authCfg.rootUrl, { body: model });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'requestOTP');
    return httpResponse ? httpResponse.body : null;
  }

  public async verifyOTP(model: VerifyOtpRequestModel): Promise<boolean> {
    const obs = localAccountVerifyOtpAsyncPost(this.http, this.authCfg.rootUrl, { body: model });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'verifyOTP');
    return httpResponse ? httpResponse.body === true : false;
  }

  private lock = new Semaphore(1);

  public async refreshToken(): Promise<void> {
    await this.performTokenRefresh();
  }

  public async logDebug(mesg: string) {
    /*  const userInfo = await UserInfoService.getUserInfo();
      if (userInfo && userInfo.email && (userInfo.email.toLocaleLowerCase().startsWith('willie'))) {
        try {
          SystemLogService.enableLogging();
          SystemLogService.logMessage('WILLIEB->' + mesg);
        } catch { }
      }*/
  }

  public async logout(reason?: string): Promise<void> {
    if (reason) {
      await this.logDebug('Logout : ' + reason);
    } else {
      await this.logDebug('Logout : no reason');
    }
    await this.clearUserData(false);

    if (Capacitor.isNativePlatform()) {
      await VivaScoreConnect.disconnect();
    }

    setTimeout(() => {
      // Final purge in case in-flight requests wrote cache during the delay
      void this.clearUserData(false).finally(() => {
        window.location.href = '/welcome';
      });
    }, 1000);
  }

  public async clearUserData(
    clearBiologin: boolean = true,
    preserveRegionAndEnvironment: boolean = true,
  ): Promise<void> {
    await UserInfoService.setUserInfo(null);
    this.clearRefresh(); //terminate timers 

    const region = await SecureStorageService.get(SecureStorageKey.CurrentRegion);
    const bioLogin = await SecureStorageService.get(SecureStorageKey.BiometricLogin);
    const environmentSettings = await SecureStorageService.get(SecureStorageKey.EnvironmentSettings);
    const lastEnvironmentFetchTime = await SecureStorageService.get(SecureStorageKey.LastEnvironmentFetchTime);
    const wearablePermissionPromptShown = await SecureStorageService.get(
      SecureStorageKey.WearablePermissionPromptShown,
    );

    await SecureStorageService.clear();
    this._weightDisplay.reset();
    this.clearLocalUserStorageKeys();

    if (bioLogin && !clearBiologin) {
      await SecureStorageService.set(SecureStorageKey.BiometricLogin, bioLogin);
    }

    if (preserveRegionAndEnvironment) {
      if (environmentSettings) {
        await SecureStorageService.set(SecureStorageKey.EnvironmentSettings, environmentSettings);
      }
      if (lastEnvironmentFetchTime) {
        await SecureStorageService.set(SecureStorageKey.LastEnvironmentFetchTime, lastEnvironmentFetchTime);
      }
      if (region) {
        await SecureStorageService.set(SecureStorageKey.CurrentRegion, region);
      }
    }

    if (wearablePermissionPromptShown) {
      await SecureStorageService.set(
        SecureStorageKey.WearablePermissionPromptShown,
        wearablePermissionPromptShown,
      );
    }

    await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");

  }

  /** Clears non-Preferences localStorage values that hold user/session UI state. */
  private clearLocalUserStorageKeys(): void {
    try {
      localStorage.removeItem('viva-score-gauge-last-score');
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key === 'observationOrder' || key.startsWith('observationOrder_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch {
      // localStorage may be unavailable in some WebView states
    }
  }

  private async resolveEffectiveUserLanguage(tokenLanguage: string | null | undefined): Promise<string | undefined> {
    const profileLanguage = await this._localeService.getCachedProfileLanguageCode();
    return normalizeCultureTag(profileLanguage) || normalizeCultureTag(tokenLanguage) || undefined;
  }

  private async setUserLoggedInAsync(): Promise<void> {
    const userInfo = await UserInfoService.getUserInfo();
    if (userInfo) {
      await this.setupRefreshTimer();
      const profileLanguage = await this._localeService.getCachedProfileLanguageCode();
      const language = profileLanguage || userInfo.language;
      if (language) {
        await this._localeService.applyFromProfileLanguage(language);
      }
    }
  }

  private refreshTimout: any | null = null;
  private clearRefresh() {
    if (this.refreshTimout) {
      clearTimeout(this.refreshTimout);
      this.refreshTimout = null;
    }
  }


  private async singleRefreshSetup() {
    const user = await UserInfoService.getUserInfo();
    if (!user?.tokenExpiry) return;
    const expMs = new Date(user.tokenExpiry).getTime();
    let delay = Math.max(0, expMs - Date.now() + 2000); // fire ~2s after expiry
    this.clearRefresh();
    this.refreshTimout = setTimeout(async () => {
      await this.performTokenRefresh(); // no "timerRefresh" escape hatch
    }, delay);
  }


  private refreshLock = new Semaphore(1); //must be another refresh timee

  private async setupRefreshTimer() {
    const result = false;
    await this.refreshLock.callFunction(async () => {
      await this.singleRefreshSetup();
    }, []);
    return result;
  }


  public async DeleteProfile(): Promise<boolean> {
    const result = await this.callApi(
      localAccountDeleteAccountDelete(this.http, this.authCfg.rootUrl),
      this.NETWORK_TIMEOUT,
      'DeleteProfile'
    );
    if (result !== null) {
      //logout and clear local storage
      await this.clearBiometricLogin();
      await this.logout();
      await SecureStorageService.clear();
      return true;
    }
    return false;
  }

  public async externalSigninOrSignup(providerName: string, isSignup: boolean,
    state: string | undefined = undefined): Promise<boolean> {

    if (!state)
      state = 'login-callback';

    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");
    if (!envConfig || !envConfig.authenticateOptions) {
      return false;
    }

    envConfig.authenticateOptions.externalAuthProviders

    const socialProvider = envConfig.authenticateOptions.externalAuthProviders?.find(a => a.providerName === providerName);
    if (!socialProvider)
      return false;

    const codeVerifier = this.generateRandomString(128);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    let flow: string | undefined | null;

    if (isSignup)
      flow = socialProvider.signupFlow;
    else
      flow = socialProvider.loginFlow;

    if (!flow)
      return false;


    let redirectUrl = environment.webBase;
    if (!redirectUrl.endsWith('/'))
      redirectUrl = redirectUrl + '/';
    redirectUrl = redirectUrl + 'login-callback';

    if (Capacitor.getPlatform() == 'ios')
      redirectUrl = environment.iosAuthRedirect;
    else if (Capacitor.getPlatform() == 'android')
      redirectUrl = environment.androidAuthRedirect;

    const scope = [
      'openid',
      'profile',
      'offline_access',
      envConfig.authenticateOptions?.scope
    ].join(' ');

    const pkceRequest: PKCERequest = {
      codeVerifier: codeVerifier,
      flow: flow,
      redirectUrl: redirectUrl,
      scope: envConfig.authenticateOptions.scope!,
      clientId: envConfig.authenticateOptions.appId!,
      baseURL: envConfig.authenticateOptions.b2CBaseURL!
    };

    await SecureStorageService.set(
      SecureStorageKey.PKCECodeVerifier,
      JSON.stringify(pkceRequest)
    );

    const authUrl = `${envConfig.authenticateOptions.b2CBaseURL}oauth2/v2.0/authorize` +
      `?p=${flow}` +
      `&client_id=${envConfig.authenticateOptions?.appId}` +
      `&redirect_uri=${encodeURIComponent(redirectUrl)}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent(scope)}` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` +
      `&state=${encodeURIComponent(state)}` +
      `&prompt=login`;

    if (Capacitor.isNativePlatform()) {
      await Browser.open({
        url: authUrl
      });
    } else {

      await Browser.open({
        url: authUrl,
        windowName: '_self',
      });
    }
    return true;
  }

  public async processSocialLoginCode(code: string | null | undefined,
    error: string | null | undefined,
    errorDescription: string | null | undefined,
    state: string | null | undefined,
    translator: TranslatorService): Promise<ExternalSignUpCallbackResult> {


    let result: ExternalSignUpCallbackResult = {
      success: false,
      errorMessage: translator.translateText('account-service.errors.generic-error'),
      redirectURL: 'home'
    };

    if (state) {
      if (state.startsWith('LINKACCOUNT')) {
        result.redirectURL = 'home/link-account';
      }
      if (state.startsWith('SIGNUP-CONSUMER')) {
        result.redirectURL = 'v2/registration/get-vivascore';
      }
    }



    if (error) {
      const decodedError = decodeURIComponent(errorDescription || '');
      let errorMessage = decodedError;
      if (decodedError.includes('AADB2C99001')) {
        errorMessage = translator.translateText('account-service.errors.account-already-inuse');
      }

      if (decodedError.includes('AADB2C90075')) {
        errorMessage = translator.translateText('account-service.errors.social-provider-failed');
      }

      if (decodedError.includes('AADB2C99002')) {
        errorMessage = translator.translateText('account-service.errors.user-does-not-exist');
      }

      result.errorMessage = errorMessage;
      result.success = false;



      return result;
    }

    if (code) {
      const storedValue = await SecureStorageService.get(SecureStorageKey.PKCECodeVerifier);
      if (!storedValue) {
        return result;
      }

      const requestType: PKCERequest = JSON.parse(storedValue) as PKCERequest;
      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: requestType.clientId,
        redirect_uri: requestType.redirectUrl,
        code: code,
        code_verifier: requestType.codeVerifier!,
        scope: 'openid profile offline_access ' + requestType.scope
      });

      try {
        const postResponse = await fetch(`${requestType.baseURL}oauth2/v2.0/token?p=${requestType.flow}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        });

        const tokenResult: PKETokenResult = await postResponse.json();
        const oldUser = await UserInfoService.getUserInfo();

        if (tokenResult && tokenResult.access_token && tokenResult.access_token.length > 0) {
          const token = this.parseJwt(tokenResult.access_token);
          const tokenExpiryUtc = new Date(token.exp * 1000).toISOString();
          let rtTtlSec = tokenResult.refresh_token_expires_in;
          if (!rtTtlSec || rtTtlSec <= 0) {
            rtTtlSec = this.DEFAULT_RT_TTL_SEC;
          }

          const nowMs = Date.now();
          const refreshTokenExpiryIso = rtTtlSec != null ? new Date(nowMs + rtTtlSec * 1000).toISOString() : (oldUser?.refreshTokenExpiry ?? null);

          const effectiveLanguage = await this.resolveEffectiveUserLanguage(token.language);

          const userInfo: UserData = {
            sub: token.oid,
            given_name: token.given_name,
            family_name: token.family_name,
            name: token.name,
            access_token: tokenResult.access_token ?? oldUser?.access_token ?? null,
            refresh_token: tokenResult.refresh_token ?? oldUser?.refresh_token ?? null,
            refreshTokenExpiry: refreshTokenExpiryIso,
            tokenExpiry: tokenExpiryUtc,
            tokenClaims: token,
            email: token.userEmailAddress!,
            language: effectiveLanguage,
            systemFunctions: token.systemFunctions,
            emailVerified: true,
            localAccount: token.localAccount,
            socialIssuer: token.idp,
            b2cFlow: token.tfp
          };


          if (state) {

            if (state.startsWith('LINKACCOUNT')) {
              const requestId = state.split('|')[1];
              if (userInfo.socialIssuer && requestId) {
                await this.linkSocialAccount(requestId, userInfo.socialIssuer, userInfo.sub);
                result.success = true;
                result.redirectURL = 'home/link-account';
                return result;
              }
            }
          }


          if (oldUser && oldUser.sub !== userInfo.sub) {
            await this.clearUserData(false);
          }
          await UserInfoService.setUserInfo(userInfo);
          await this.setUserLoggedInAsync();

          //investigate state variable to see what to do
          if (state) {
            if (state.startsWith('SIGNUP-CONSUMER')) {
              result.success = true;
              result.redirectURL = 'v2/registration/confirm-profile';
              return result;
            }
          }
          result.success = true;
          result.redirectURL = 'home';
        }
      } catch (error) {
        console.error('Token exchange failed:', error);
        result.success = false;
      }
    }
    return result;
  }

  public async getLinkedSocialAccounts(): Promise<SocialAccountModel[] | null> {
    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");
    if (!envConfig) {
      return null;
    }

    const obs = localAccountLinkedSocialAccountGet(this.http, this.authCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'getLinkedSocialAccounts');
    return httpResponse ? httpResponse.body : null;
  }

  public async requestAccountLink(providerName: string): Promise<boolean> {
    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");
    if (!envConfig) {
      return false;
    }

    const obs = localAccountRequestAccountLinkPost(this.http, this.authCfg.rootUrl);
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'requestAccountLink');
    const linkRequestId = httpResponse ? httpResponse.body : null;

    if (linkRequestId && typeof linkRequestId === 'string') {
      return await this.externalSigninOrSignup(providerName, true, 'LINKACCOUNT|' + linkRequestId);
    }

    return false;
  }

  public async linkSocialAccount(linkRequestId: string, issuer: string, sourceUserB2CId: string): Promise<SocialAccountModel[] | null> {
    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");
    if (!envConfig) {
      return null;
    }
    const userInfo = await UserInfoService.getUserInfo();
    if (!userInfo) {
      return null;
    }

    const model: LinkSocialAccountRequestModel = {
      linkToken: linkRequestId,
      issuer: issuer,
      sourceUserB2CId: sourceUserB2CId
    }

    const obs = localAccountLinkAccountPost(this.http, this.authCfg.rootUrl, { body: model });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'linkSocialAccount');
    return httpResponse ? httpResponse.body : null;
  }

  public async unlinkSocialAccount(account: SocialAccountModel): Promise<SocialAccountModel[] | null> {
    const envConfig = await this._EnvironmentConfigService.EnsureNetworkConfiguration("Auth Service");
    if (!envConfig) {
      return null;
    }

    const userInfo = await UserInfoService.getUserInfo();
    if (!userInfo) {
      return null;
    }

    const model: UnlinkSocialAccountRequestModel = {
      issuer: account.issuer!,
    }

    const obs = localAccountUnlinkAccountPost(this.http, this.authCfg.rootUrl, { body: model });
    const httpResponse = await this.callApi(obs, this.NETWORK_TIMEOUT, 'unlinkSocialAccount');
    return httpResponse ? httpResponse.body : null;
  }

  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }
    return result;
  }

  private async generateCodeChallenge(codeVerifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  private parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    let payload = JSON.parse(jsonPayload);

    if (!payload.personId) {
      payload.personId = payload.extension_PersonId;
    }

    if (!payload.organizationId)
      payload.organizationId = payload.extension_OrganizationId;

    if (!payload.language)
      payload.language = payload.extension_Language;

    if (!payload.userEmailAddress)
      payload.userEmailAddress = payload.extension_UserEmailAddress;

    if (!payload.systemFunctions && payload.extension_SystemFunctions)
      payload.systemFunctions = payload.extension_SystemFunctions.split("|");

    if (!payload.idp)
      payload.localAccount = true;
    else
      payload.localAccount = false;

    return payload;
  }


private refreshInFlight: Promise<void> | null = null;
private async performTokenRefresh(): Promise<void> {
  // If a refresh is already running, reuse it.
  if (this.refreshInFlight) {
    await this.refreshInFlight;
    return;
  }

  // Start a new refresh, and ensure the guard is cleared when done.
  this.refreshInFlight = this._performTokenRefreshInner()
    .catch(async (err) => {
      // propagate but also log if you want
      await this.logDebug(`REFRESH TOKEN unhandled error: ${err?.message ?? err}`);
      throw err;
    })
    .finally(() => {
      this.refreshInFlight = null;
    });

  await this.refreshInFlight;
}

  private async _performTokenRefreshInner(): Promise<void> {
    const status = await Network.getStatus();
    if (!status.connected) {
      await this.logDebug("REFRESH TOKEN skipped - no network connection.");
      return;
    }

      try {
        const userInfo = await UserInfoService.getUserInfo();
        if(!userInfo || !userInfo.access_token|| !userInfo.refresh_token) //we never had a logged in user go empty
          return;

        const oldUser = userInfo;

        const expired = await this.tokenExpired();
        if (!expired) return;

        if (userInfo && userInfo.refreshTokenExpiry && userInfo.refreshTokenExpiry.length > 0) {
          if (userInfo?.refreshTokenExpiry && Date.now() >= new Date(userInfo.refreshTokenExpiry).getTime() - 60_000) {
            await this.logout('Refresh token expired');
            return;
          }
        }


        const tfp = userInfo.tokenClaims?.tfp || null;
        const env = await this._EnvironmentConfigService.EnsureNetworkConfigurationForToken();
        const refreshOptions: RefreshTokenRequestModel = {
          applicationId: env!.authenticateOptions!.appId!,
          refreshToken: userInfo.refresh_token,
          tfp
        };

        this.tokenRefreshRetry++;
        await this.logDebug(`REFRESH TOKEN attempt: ${this.tokenRefreshRetry}`);

        const httpResponse = await firstValueFrom(
          localAccountRefreshTokenPost(this.http, this.authCfg.rootUrl, { body: refreshOptions })
            .pipe(timeout(this.REFRESH_TIMEOUT))
        );
        const response = httpResponse.body;

        if (!response?.access_token || response?.error) {
          await this.logDebug(`REFRESH TOKEN failed - invalid 200 body: ${response?.error ?? 'missing access_token'}`);
          if (this.tokenRefreshRetry > 1) { // or 0 if you prefer
            await this.logout('Refresh failed: invalid response');
            this.tokenRefreshRetry = 0;
          } else {
            return this.scheduleRefreshRetry();
          }
          return;
        }

        const token = this.parseJwt(response.access_token);
        const tokenExpiryUtc = new Date(token.exp * 1000).toISOString();

        let rtTtlSec = response.refresh_token_expires_in;
        if (!rtTtlSec || rtTtlSec <= 0) {
          rtTtlSec = this.DEFAULT_RT_TTL_SEC;
        }

        const nowMs = Date.now();
        const refreshTokenExpiryIso = rtTtlSec != null ? new Date(nowMs + rtTtlSec * 1000).toISOString() : (oldUser?.refreshTokenExpiry ?? null);

        const effectiveLanguage = await this.resolveEffectiveUserLanguage(token.language);

        const tokenUser: UserData = {
          sub: token.oid,
          given_name: token.given_name,
          family_name: token.family_name,
          name: token.name,
          access_token: response.access_token ?? oldUser?.access_token ?? null,
          refresh_token: response.refresh_token ?? oldUser?.refresh_token ?? null,
          refreshTokenExpiry: refreshTokenExpiryIso,
          tokenExpiry: tokenExpiryUtc,
          tokenClaims: token,
          email: token.userEmailAddress!,
          language: effectiveLanguage,
          systemFunctions: token.systemFunctions,
          emailVerified: response.emailVerified,
          localAccount: token.localAccount,
          socialIssuer: token.idp,
          b2cFlow: token.tfp
        };

        if (oldUser && oldUser.sub !== tokenUser.sub) {
          await this.clearUserData(true);
        }

        await UserInfoService.setUserInfo(tokenUser);
        await this.setUserLoggedInAsync();
        await this.logDebug("REFRESH TOKEN success: user session updated");
      } catch (err: any) {
        // Angular HttpErrorResponse shape
        const httpStatus = err?.status as number | undefined;
        const body = err?.error ?? {};
        const code = (body?.error ?? '').toString();
        const desc = (body?.error_description ?? body?.message ?? err?.message ?? 'Unknown error').toString();

        // Helpers
        const isInvalidGrant = (httpStatus === 400 || httpStatus === 401) && code === 'invalid_grant';
        const isAccountDisabled = httpStatus === 403 && code === 'account_disabled';
        const isUserNotFound = httpStatus === 404 && code === 'user_not_found';
        const isOrgMismatch = httpStatus === 409 && code === 'org_mismatch';
        const isUpstream = httpStatus === 502 || httpStatus === 503; // transient

        if (isOrgMismatch) {
          // ✅ show message BEFORE logout
          await this._PromptService.showError(
            'Your organisation has changed. Please sign in again.'
          );
          await this.logDebug(`REFRESH TOKEN failed: ORG_MISMATCH → logout`);
          await this.logout('Organisation changed');
          this.tokenRefreshRetry = 0;
          return;
        }

        if (isAccountDisabled) {
          await this._PromptService.showError('Your account is disabled. Please contact support.');
          await this.logout('Account disabled');
          this.tokenRefreshRetry = 0;
          return;
        }

        if (isUserNotFound) {
          await this._PromptService.showError('We could not find your account. Please sign in again.');
          await this.logout('User not found');
          this.tokenRefreshRetry = 0;
          return;
        }

        if (isInvalidGrant) {
          await this.logDebug(`REFRESH TOKEN failed: INVALID_GRANT → logout`);
          await this.logout('Refresh failed: invalid_grant');
          this.tokenRefreshRetry = 0;
          return;
        }

        if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
          await this.logDebug(`REFRESH TOKEN failed (HTTP ${httpStatus}, ${code}): ${desc}`);
          await this.logout(`Refresh failed: ${code || httpStatus}`);
          this.tokenRefreshRetry = 0;
          return;
        }

        // Transient / unknown errors → retry (bounded)
        const message = desc;
        if (isUpstream || !httpStatus || httpStatus >= 500) {
          if (this.tokenRefreshRetry > this.MAX_REFRESH_RETRIES) {
            await this._PromptService.showNetworkConnectionError(err, 'refresh');
            await this.logout(`Refresh failed after ${this.tokenRefreshRetry} retries: ${message}`);
            this.tokenRefreshRetry = 0;
          } else {
            await this.logDebug(`REFRESH TOKEN retry scheduled: attempt ${this.tokenRefreshRetry}, error: ${message}`);
            return this.scheduleRefreshRetry();
          }
          return;
        }

      }
   
  }

  private scheduleRefreshRetry(): void {
    const delay = Math.min(
      this.RETRY_BACKOFF_BASE * Math.pow(2, this.tokenRefreshRetry),
      this.RETRY_BACKOFF_MAX
    );

    this.clearRefresh(); // Prevent multiple retry timers

    this.refreshTimout = setTimeout(async () => {
      await this.performTokenRefresh();
    }, delay);

    this.logDebug(`Scheduled refresh retry in ${delay / 1000}s`);
  }
}

export interface ExternalSignUpCallbackResult {
  success: boolean,
  redirectURL: string,
  errorMessage: string
}
interface PKCERequest {
  codeVerifier: string,
  flow: string,
  redirectUrl: string,
  scope: string,
  clientId: string,
  baseURL: string
}

interface PKETokenResult {
  access_token: string,
  id_token: string,
  token_type: string,
  expires_in: number,
  expires_on: number,
  resource: string,
  id_token_expires_in: number,
  profile_info: string,
  scope: string,
  refresh_token: string,
  refresh_token_expires_in: number
}


// password-policy.ts
export const B2C_PASSWORD_REGEX =
  /^((?=(?:.*[a-z]))(?=(?:.*[A-Z]))(?=(?:.*\d))|(?=(?:.*[a-z]))(?=(?:.*[A-Z]))(?=(?:.*[^A-Za-z0-9]))|(?=(?:.*[a-z]))(?=(?:.*\d))(?=(?:.*[^A-Za-z0-9]))|(?=(?:.*[A-Z]))(?=(?:.*\d))(?=(?:.*[^A-Za-z0-9])))(?:[A-Za-z\d@#$%^&*\-_+=[\]{}|\\:',?/`~"();!]|\\.(?!@)){8,24}$/;